import React, { useState, useEffect } from 'react';
import { ProbationInstructionItem, Employee, ProbationInstructionItemList } from '../types/models';
import { ProbationInstructionItemService } from '../services/probationInstructionItemService';
import { EmployeeService } from '../services/employeeService';
import { ProbationInstructionItemForm } from '../components/forms/ProbationInstructionItemForm';
import { API_BASE_URL } from '../services/api';

export const ProbationInstructionItemsPage: React.FC = () => {
  const [instructions, setInstructions] = useState<ProbationInstructionItemList[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<number | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingInstruction, setEditingInstruction] = useState<ProbationInstructionItem | null>(null);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [createdInstruction, setCreatedInstruction] = useState<ProbationInstructionItem | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (employees.length > 0) {
      loadInstructions();
    }
  }, [selectedEmployeeId, employees]);

  const loadData = async () => {
    try {
      setLoading(true);

      // Загружаем сотрудников
      const employeesResponse = await EmployeeService.getAll();

      if (employeesResponse.success) {
        setEmployees(employeesResponse.data);
      }

      // Загружаем стажировки
      await loadInstructions();
    } catch (err) {
      console.error('Ошибка загрузки данных:', err);
      setError('Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  };

  const loadInstructions = async () => {
    try {
      let response;

      if (selectedEmployeeId) {
        response = await ProbationInstructionItemService.getByEmployee(selectedEmployeeId);
      } else {
        // По умолчанию получаем все стажировки
        response = await ProbationInstructionItemService.getAll();
      }

      if (response.success) {
        setInstructions(response.data);
        setError(null);
      } else {
        setError(response.message || 'Ошибка загрузки стажировок');
      }
    } catch (err) {
      setError('Ошибка загрузки данных');
    }
  };

  const handleCreate = () => {
    setEditingInstruction(null);
    setShowForm(true);
  };

  const handleEdit = (instruction: ProbationInstructionItemList) => {
    // Convert to full ProbationInstructionItem for editing
    const fullInstruction: ProbationInstructionItem = {
      ...instruction,
      employee: employees.find(e => e.id === instruction.employeeId),
      responsibleEmployee: employees.find(e => e.id === instruction.responsibleEmployeeId),
      startDate: instruction.startDate,
      finishDate: instruction.finishDate,
      docDate: instruction.docDate,
      shiftCount: instruction.shiftCount,
    };
    setEditingInstruction(fullInstruction);
    setShowForm(true);
  };

  const handleDelete = async (instruction: ProbationInstructionItemList) => {
    const employeeName = getEmployeeName(instruction);

    if (window.confirm(`Вы уверены, что хотите удалить стажировку для "${employeeName}"?`)) {
      try {
        const response = await ProbationInstructionItemService.delete(instruction.id);
        if (response.success) {
          loadInstructions(); // Перезагрузить список
        } else {
          setError(response.message || 'Ошибка удаления');
        }
      } catch (err) {
        setError('Ошибка удаления стажировки');
      }
    }
  };

  const handleFormSuccess = (createdItem?: ProbationInstructionItem) => {
    setShowForm(false);
    setEditingInstruction(null);

    // Если это создание новой стажировки, показать диалог экспорта
    if (createdItem && !editingInstruction) {
      setCreatedInstruction(createdItem);
      setShowExportDialog(true);
    } else {
      // Для редактирования просто перезагрузить список
      loadInstructions();
    }
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingInstruction(null);
  };

  const handleExportDialogYes = async () => {
    if (createdInstruction) {
      await handleExport(createdInstruction.id);
    }
    setShowExportDialog(false);
    setCreatedInstruction(null);
    loadInstructions(); // Перезагрузить список после экспорта
  };

  const handleExportDialogNo = () => {
    setShowExportDialog(false);
    setCreatedInstruction(null);
    loadInstructions(); // Перезагрузить список
  };

  const handleExport = async (instructionId: number) => {
    try {
      const response = await fetch(`${API_BASE_URL}/ProbationInstructionItem/DownloadFile?id=${instructionId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        // Получаем blob из ответа
        const blob = await response.blob();

        // Создаем URL для blob
        const url = window.URL.createObjectURL(blob);

        // Создаем временную ссылку для скачивания
        const link = document.createElement('a');
        link.href = url;

        // Получаем имя файла из заголовка Content-Disposition или используем дефолтное
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = 'Журнал_регистрации_стажировки.docx';

        if (contentDisposition) {
          // Сначала проверяем filename*=UTF-8'' формат (новый стандарт)
          const filenameStarMatch = contentDisposition.match(/filename\*=UTF-8''([^;\n]+)/);
          if (filenameStarMatch && filenameStarMatch[1]) {
            filename = decodeURIComponent(filenameStarMatch[1]);
          } else {
            // Fallback на старый формат filename=
            const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
            if (filenameMatch && filenameMatch[1]) {
              filename = filenameMatch[1].replace(/['"]/g, '');
              // Декодируем URL-кодированные символы
              filename = decodeURIComponent(filename);
            }
          }
        }

        link.download = filename;

        // Добавляем ссылку в DOM, кликаем и удаляем
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Освобождаем URL
        window.URL.revokeObjectURL(url);
      } else {
        console.error('Ошибка при скачивании файла:', response.statusText);
        alert('Ошибка при скачивании файла');
      }
    } catch (error) {
      console.error('Ошибка при скачивании файла:', error);
      alert('Ошибка при скачивании файла');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ru-RU');
  };

  const getEmployeeName = (instruction: ProbationInstructionItemList) => {
    const employee = employees.find(e => e.id === instruction.employeeId);
    return employee
      ? `${employee.surname} ${employee.name}`
      : `Сотрудник ${instruction.employeeId}`;
  };

  const getResponsibleEmployeeName = (instruction: ProbationInstructionItemList) => {
    const employee = employees.find(e => e.id === instruction.responsibleEmployeeId);
    return employee
      ? `${employee.surname} ${employee.name}`
      : `Сотрудник ${instruction.responsibleEmployeeId}`;
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-error">
        {error}
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Стажировка</h1>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <select
            className="form-input"
            style={{ width: '200px' }}
            value={selectedEmployeeId || ''}
            onChange={(e) => {
              setSelectedEmployeeId(Number(e.target.value) || null);
            }}
          >
            <option value="">Все сотрудники</option>
            {employees.map((employee) => (
              <option key={employee.id} value={employee.id}>
                {`${employee.surname} ${employee.name} ${employee.patronymic}`.trim()}
              </option>
            ))}
          </select>

          {selectedEmployeeId && (
            <button
              className="btn btn-secondary"
              onClick={() => {
                setSelectedEmployeeId(null);
              }}
            >
              Сбросить фильтр
            </button>
          )}

          <button className="btn btn-primary" onClick={handleCreate}>
            Добавить стажировку
          </button>
        </div>
      </div>

      <div className="card">
        <table className="table">
          <thead>
            <tr>
              <th>Дата начала</th>
              <th>Дата окончания</th>
              <th>Дата распоряжения</th>
              <th>Количество смен</th>
              <th>Сотрудник</th>
              <th>Ответственный</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {instructions.map((instruction) => (
              <tr key={instruction.id}>
                <td>{formatDate(instruction.startDate)}</td>
                <td>{formatDate(instruction.finishDate)}</td>
                <td>{formatDate(instruction.docDate)}</td>
                <td>{instruction.shiftCount}</td>
                <td>{getEmployeeName(instruction)}</td>
                <td>{getResponsibleEmployeeName(instruction)}</td>
                <td>
                  <button
                    className="btn btn-primary"
                    style={{ marginRight: '5px' }}
                    onClick={() => handleExport(instruction.id)}
                  >
                    Экспорт
                  </button>
                  <button
                    className="btn btn-secondary"
                    style={{ marginRight: '5px' }}
                    onClick={() => handleEdit(instruction)}
                  >
                    Редактировать
                  </button>
                  <button
                    className="btn btn-danger"
                    onClick={() => handleDelete(instruction)}
                  >
                    Удалить
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {instructions.length === 0 && (
          <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
            {selectedEmployeeId
              ? 'Нет стажировок для выбранного сотрудника.'
              : 'В базе данных пока нет стажировок. Создайте первую стажировку, нажав кнопку "Добавить стажировку".'
            }
          </div>
        )}
      </div>

      {showForm && (
        <ProbationInstructionItemForm
          instruction={editingInstruction || undefined}
          employeeId={selectedEmployeeId || undefined}
          onSuccess={handleFormSuccess}
          onCancel={handleFormCancel}
        />
      )}

      {showExportDialog && (
        <div className="modal-overlay" style={{ zIndex: 1100 }}>
          <div
            className="modal-content"
            style={{ maxWidth: '400px', textAlign: 'center' }}
          >
            <h3>Экспортировать в Word?</h3>
            <p>Стажировка успешно создана. Хотите экспортировать её в документ Word?</p>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginTop: '20px' }}>
              <button
                className="btn btn-primary"
                onClick={handleExportDialogYes}
              >
                Да, экспортировать
              </button>
              <button
                className="btn btn-secondary"
                onClick={handleExportDialogNo}
              >
                Нет, спасибо
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};